/**
 * DraggableTaskList - Phase 7
 * 
 * Enables drag-and-drop reordering of tasks using @dnd-kit.
 * Features:
 * - Smooth drag animations
 * - Touch and mouse support
 * - Keyboard accessible (tab + space/enter to pick up, arrows to move)
 * - Visual feedback during drag
 * - Persists order to Firestore
 * 
 * @module DraggableTaskList
 */

import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Square, CheckSquare } from 'lucide-react';
import TaskCard from './TaskCard';
import SwipeableTaskCard from './SwipeableTaskCard';

// ============================================================================
// SORTABLE TASK ITEM
// ============================================================================

function SortableTaskItem({ 
  task, 
  onToggle, 
  onEdit, 
  onDelete, 
  onArchive,
  onUnarchive,
  onClick,
  enableSwipe,
  isDragMode,
  isSelectMode,
  isSelected,
  onSelect
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };

  // Render the task card with drag handle
  const TaskComponent = enableSwipe ? SwipeableTaskCard : TaskCard;

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag Handle - only visible in drag mode */}
      {isDragMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 opacity-60 hover:opacity-100 transition-opacity bg-gradient-to-r from-gray-100 to-transparent rounded-l-xl"
          aria-label="Drag to reorder"
        >
          <GripVertical size={18} className="text-gray-400" />
        </div>
      )}
      
      {/* Selection Checkbox - only visible in select mode */}
      {isSelectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(task.id);
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center z-10 transition-all"
          aria-label={isSelected ? "Deselect task" : "Select task"}
        >
          {isSelected ? (
            <CheckSquare size={22} className="text-blue-600" />
          ) : (
            <Square size={22} className="text-gray-400 hover:text-gray-600" />
          )}
        </button>
      )}
      
      <div className={isDragMode ? 'pl-8' : isSelectMode ? 'pl-10' : ''}>
        <TaskComponent
          task={task}
          onToggle={onToggle}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onClick={onClick}
          enableSwipe={enableSwipe && !isDragMode && !isSelectMode}
        />
      </div>
    </div>
  );
}

// ============================================================================
// DRAG OVERLAY CONTENT
// ============================================================================

function DragOverlayContent({ task }) {
  if (!task) return null;

  return (
    <div className="opacity-90 shadow-2xl transform scale-105 rounded-xl">
      <TaskCard task={task} />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DraggableTaskList({
  tasks,
  onReorder,
  onToggle,
  onEdit,
  onDelete,
  onArchive,
  onUnarchive,
  onClick,
  enableSwipe = false,
  isDragMode = false,
  isSelectMode = false,
  selectedTasks = [],
  onToggleSelect,
  className = '',
}) {
  const [activeId, setActiveId] = useState(null);
  // Local state for immediate visual feedback during drag
  const [localTasks, setLocalTasks] = useState(tasks);
  
  // Sync local tasks with props when tasks change (but not during drag)
  useEffect(() => {
    if (!activeId) {
      setLocalTasks(tasks);
    }
  }, [tasks, activeId]);

  // Configure sensors for different input methods
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms hold before drag starts on touch
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get the active task being dragged
  const activeTask = activeId ? localTasks.find(t => t.id === activeId) : null;

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handle drag end - reorder items
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localTasks.findIndex(t => t.id === active.id);
    const newIndex = localTasks.findIndex(t => t.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(localTasks, oldIndex, newIndex);
      
      // Update local state immediately for visual feedback
      setLocalTasks(newOrder);
      
      // Call the reorder callback with new order
      if (onReorder) {
        onReorder(newOrder);
      }
    }
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveId(null);
    // Reset to original order on cancel
    setLocalTasks(tasks);
  };

  // If not in drag mode, just render the tasks normally (with optional selection support)
  if (!isDragMode) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
        {tasks.map(task => {
          const TaskComponent = enableSwipe && !isSelectMode ? SwipeableTaskCard : TaskCard;
          const isSelected = selectedTasks.includes(task.id);
          
          return (
            <div key={task.id} className="relative">
              {/* Selection Checkbox */}
              {isSelectMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(task.id);
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center z-10 transition-all"
                  aria-label={isSelected ? "Deselect task" : "Select task"}
                >
                  {isSelected ? (
                    <CheckSquare size={22} className="text-blue-600" />
                  ) : (
                    <Square size={22} className="text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              )}
              
              <div className={isSelectMode ? 'pl-10' : ''}>
                <TaskComponent
                  task={task}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onArchive={onArchive}
                  onUnarchive={onUnarchive}
                  onClick={onClick}
                  enableSwipe={enableSwipe && !isSelectMode}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Render with drag and drop enabled
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={localTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
          {localTasks.map(task => (
            <SortableTaskItem
              key={task.id}
              task={task}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onClick={onClick}
              enableSwipe={enableSwipe}
              isDragMode={isDragMode}
              isSelectMode={isSelectMode}
              isSelected={selectedTasks.includes(task.id)}
              onSelect={onToggleSelect}
            />
          ))}
        </div>
      </SortableContext>

      {/* Drag Overlay - shows a preview while dragging */}
      <DragOverlay>
        <DragOverlayContent task={activeTask} />
      </DragOverlay>
    </DndContext>
  );
}
